import { useEffect } from "react";
import { useAuth } from "react-oidc-context";

import { LAST_ROUTE, routes } from "../constants";
import { UserDataProvider } from "../contexts/userDataContext";
import { ServerError } from "../pages/authPages";
import { isTech } from "../utils";
import { Preloader } from "./preloader";

// Session-storage flag guarding the OIDC self-heal redirect (see error branch).
const OIDC_RETRY = "oidc_retry";

export const oidcConfig = {
	authority:
		(isTech
			? import.meta.env.VITE_STS_AUTHORITY_TECH
			: import.meta.env.VITE_STS_AUTHORITY) ?? "",
	client_id: import.meta.env.VITE_CLIENT_ID ?? "",
	redirect_uri: window.location.origin + routes.signInOidc.path,
	client_secret:
		(import.meta.env.VITE_APP_TITLE || "") +
		(import.meta.env.VITE_DEBUG_INFO || ""),
	response_type: import.meta.env.VITE_RESPONSE_TYPE,
	scope: "openid crmApi blastDataApi IdentityServerApi offline_access",
	grant_type: "code",
	post_logout_redirect_uri:
		window.location.origin + routes.signOutCallbackOidc.path,
	automaticSilentRenew: !import.meta.env.DEV,
	monitorSession: !import.meta.env.DEV,
};

export const Auth = ({
	children,
	noRefresh = false,
	isReturnIfNotAuth = false,
	isWaitForUser = false,
	isRegister = false,
}: {
	children: any;
	noRefresh?: boolean;
	isReturnIfNotAuth?: boolean;
	isWaitForUser?: boolean;
	isRegister?: boolean;
}) => {
	const {
		isAuthenticated,
		activeNavigator,
		isLoading,
		error,
		events,
		signinRedirect,
		signinSilent,
		removeUser,
	} = useAuth();

	useEffect(() => {
		// the `return` is important - addAccessTokenExpiring() returns a cleanup function
		return events.addAccessTokenExpiring(async () => {
			console.log("addAccessTokenExpiring");
			try {
				await signinSilent();
			} catch (error) {
				console.error(error);
				window.location.href = "/";
			}
		});
	}, [events, signinSilent]);

	useEffect(() => {
		// the `return` is important - addUserSignedOut() returns a cleanup function
		return events.addUserSignedOut(() => {
			console.log("addUserSignedOut");
			// https://github.com/IdentityModel/oidc-client-js/issues/729
			// band aid fix so we don't have to do weird cookie nonsense
			if (!import.meta.env.DEV) {
				console.log("removing user");

				removeUser();
			}
		});
	}, [events, removeUser]);

	useEffect(() => {
		// the `return` is important - addUserSignedIn() returns a cleanup function
		return events.addUserSignedIn(() => {
			console.log("addUserSignedIn");
			removeUser();
		});
	}, [events, removeUser]);

	useEffect(() => {
		// A completed sign-in clears the self-heal retry guard below.
		if (isAuthenticated) sessionStorage.removeItem(OIDC_RETRY);
	}, [isAuthenticated]);

	if (
		isAuthenticated ||
		isReturnIfNotAuth ||
		import.meta.env.MODE === "test" ||
		import.meta.env.VITE_MOCK_MODE === "true"
	) {
		return (
			<UserDataProvider
				isWaitForUser={isWaitForUser}
				isReturnIfNotAuth={isReturnIfNotAuth}
			>
				{children}
			</UserDataProvider>
		);
	}

	if (isLoading) {
		return <Preloader />;
	}

	switch (activeNavigator) {
		case "signinSilent":
			return <Preloader />;
		case "signoutRedirect":
			return <Preloader />;
		default:
			break;
	}

	if (error) {
		console.log("error :>> ", error);
		// "No matching state found in storage" is recoverable — the sign-in
		// state was already consumed or lost (duplicate callback, back-button,
		// or a cross-tab login). Restart the flow once rather than dead-ending
		// on the error screen. The retry guard stops an infinite redirect loop
		// if sign-in genuinely can't complete; it's cleared on success above.
		if (error.message?.includes("No matching state")) {
			if (!sessionStorage.getItem(OIDC_RETRY)) {
				sessionStorage.setItem(OIDC_RETRY, "1");
				signinRedirect();
				return <Preloader />;
			}
		}
		return <ServerError message={error.message} error={error} />;
	}

	if (noRefresh) {
		return null;
	}

	sessionStorage.setItem(LAST_ROUTE, window.location.pathname);
	signinRedirect({
		extraQueryParams: {
			register: isRegister,
		},
	});

	return null;
};
